/**
 * Validates file type by checking the actual file signature (magic bytes)
 * instead of relying on file extension which can be spoofed.
 *
 * @param file - The file to validate
 * @returns Promise<string> - The actual file type detected from signature
 */
export const getFileTypeFromSignature = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(
        0,
        4
      );
      const header = Array.from(arr)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const signatures: { [key: string]: string } = {
        '89504e47': 'png',
        ffd8ffe0: 'jpeg', // JFIF
        ffd8ffe1: 'jpeg', // EXIF
        ffd8ffe2: 'jpeg', // Canon
        ffd8ffe3: 'jpeg', // Samsung
        ffd8ffe8: 'jpeg', // SPIFF
        ffd8ffdb: 'jpeg', // Samsung
        '25504446': 'pdf',
        '504b0304': 'docx', // DOCX (ZIP-based Office Open XML formats)
      };

      resolve(signatures[header.substring(0, 8)] || 'unknown');
    };
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};

/**
 * Validates if the actual file type matches the expected MIME type
 *
 * @param file - The file to validate
 * @param allowedTypes - Array of allowed MIME types
 * @returns Promise<boolean> - True if file signature matches expected type
 */
export const validateFileSignature = async (
  file: File,
  allowedTypes: string[]
): Promise<boolean> => {
  const actualType = await getFileTypeFromSignature(file);
  const expectedType = file.type.split('/')[1];

  // Check if the file type is allowed
  if (!allowedTypes.includes(file.type)) {
    return false;
  }

  // Validate signature matches MIME type
  return (
    actualType === expectedType ||
    (actualType === 'jpeg' && expectedType === 'jpeg') ||
    (actualType === 'docx' &&
      file.type.startsWith('application/vnd.openxmlformats'))
  );
};
